import { useState } from 'react';
import { Link, useNavigate } from 'react-router-dom';
import { motion } from 'framer-motion';
import { ArrowRight, Mic, Brain, BarChart2, Shield, Sparkles, Clock, Check } from 'lucide-react';
import { Button } from '@/components/ui/button';
import { Input } from '@/components/ui/input';
import { toast } from 'sonner';
import { betaApi } from '@/lib/api';
import { useLanguage } from '@/context/LanguageContext';
import LanguageSelector from '@/components/LanguageSelector';

const featureIcons = [Mic, Brain, BarChart2, Shield, Sparkles, Clock];

export default function LandingPage() {
  const [email, setEmail] = useState('');
  const [loading, setLoading] = useState(false);
  const { t } = useLanguage();

  const features = [
    { icon: Mic, title: t('feature_input'), description: t('feature_input_desc') },
    { icon: Brain, title: t('feature_coach'), description: t('feature_coach_desc') },
    { icon: BarChart2, title: t('feature_analytics'), description: t('feature_analytics_desc') },
    { icon: Shield, title: t('feature_privacy'), description: t('feature_privacy_desc') },
    { icon: Sparkles, title: t('feature_insights'), description: t('feature_insights_desc') },
    { icon: Clock, title: t('feature_availability'), description: t('feature_availability_desc') },
  ];

  const benefits = [
    { stat: '27%', text: t('benefit_1') },
    { stat: '23%', text: t('benefit_2') },
    { stat: '60-70%', text: t('benefit_3') },
    { stat: '24h', text: t('benefit_4') },
  ];

  const handleBetaSignup = async (e) => {
    e.preventDefault();
    if (!email) return;
    
    setLoading(true);
    try {
      const response = await betaApi.signup(email);
      toast.success(response.data.message);
      setEmail('');
    } catch (error) {
      toast.error('Failed to sign up. Please try again.');
    } finally {
      setLoading(false);
    }
  };

  return (
    <div className="min-h-screen bg-navy-950">
      {/* Navigation */}
      <nav className="fixed top-0 w-full z-50 backdrop-blur-lg border-b border-white/5 bg-navy-950/80">
        <div className="max-w-7xl mx-auto px-6 py-4">
          <div className="flex items-center justify-between">
            <Link to="/" className="flex items-center gap-3">
              <img 
                src="https://customer-assets.emergentagent.com/job_7d9bc2e5-ff38-4e06-b101-9f0eb41c2616/artifacts/w350as9h_LOGOvrn-02.png" 
                alt="VerbNotes" 
                className="h-14 md:h-16"
              />
            </Link>
            <div className="flex items-center gap-3">
              <LanguageSelector variant="ghost" />
              <Link to="/login">
                <Button variant="ghost" className="text-slate-300 hover:text-cyan" data-testid="nav-login-btn">
                  {t('nav_login')}
                </Button>
              </Link>
              <Link to="/register">
                <Button className="bg-cyan text-navy-950 hover:bg-cyan-300 rounded-full px-6 shadow-[0_0_20px_rgba(0,212,255,0.3)]" data-testid="nav-signup-btn">
                  {t('nav_get_started')}
                </Button>
              </Link>
            </div>
          </div>
        </div>
      </nav>

      {/* Hero Section */}
      <section className="relative pt-36 pb-20 px-6 overflow-hidden">
        {/* Background glow */}
        <div className="absolute inset-0 bg-[radial-gradient(circle_at_50%_0%,rgba(0,212,255,0.15)_0%,transparent_70%)]" />
        
        <div className="max-w-5xl mx-auto text-center relative">
          <motion.div
            initial={{ opacity: 0, y: 20 }}
            animate={{ opacity: 1, y: 0 }}
            transition={{ duration: 0.6 }}
          >
            <h1 className="font-heading text-5xl md:text-7xl font-light text-white mb-6 tracking-tight">
              {t('hero_title_1')}{' '}
              <span className="text-cyan">{t('hero_title_2')}</span>
            </h1>
            <p className="text-lg md:text-xl text-slate-300 max-w-2xl mx-auto mb-10 leading-relaxed">
              {t('hero_subtitle')}
            </p>
            
            <div className="flex flex-col sm:flex-row items-center justify-center gap-4">
              <Link to="/register">
                <Button 
                  size="lg" 
                  className="bg-cyan text-navy-950 hover:bg-cyan-300 rounded-full px-8 py-6 text-lg font-semibold shadow-[0_0_30px_rgba(0,212,255,0.4)] hover:shadow-[0_0_40px_rgba(0,212,255,0.6)] transition-all"
                  data-testid="hero-cta-btn"
                >
                  {t('hero_cta')}
                  <ArrowRight className="ml-2" size={20} />
                </Button>
              </Link>
              <Button 
                variant="outline" 
                size="lg"
                className="border-gold text-gold hover:bg-gold/10 rounded-full px-8 py-6 text-lg"
                onClick={() => document.getElementById('features').scrollIntoView({ behavior: 'smooth' })}
                data-testid="hero-learn-more-btn"
              >
                {t('hero_learn_more')}
              </Button>
            </div>
          </motion.div>

          {/* App Preview */}
          <motion.div
            initial={{ opacity: 0, y: 40 }}
            animate={{ opacity: 1, y: 0 }}
            transition={{ duration: 0.8, delay: 0.3 }}
            className="mt-16 relative"
          >
            <div className="glass rounded-2xl p-6 md:p-8 max-w-3xl mx-auto">
              <div className="flex items-center gap-3 mb-6">
                <div className="w-3 h-3 rounded-full bg-red-500" />
                <div className="w-3 h-3 rounded-full bg-yellow-500" />
                <div className="w-3 h-3 rounded-full bg-green-500" />
                <span className="text-slate-500 text-sm ml-4">VerbNotes {t('nav_dashboard')}</span>
              </div>
              <div className="grid grid-cols-1 md:grid-cols-2 gap-4">
                <div className="bg-navy-900/50 rounded-xl p-4 border border-white/5">
                  <p className="text-cyan text-sm font-medium mb-2">{t('dashboard_avg_mood')}</p>
                  <p className="text-4xl font-heading text-white">7.2</p>
                  <p className="text-slate-400 text-sm mt-1">+12% {t('analytics_vs_week')}</p>
                </div>
                <div className="bg-navy-900/50 rounded-xl p-4 border border-white/5">
                  <p className="text-gold text-sm font-medium mb-2">{t('dashboard_insights')}</p>
                  <p className="text-slate-200 text-sm leading-relaxed">
                    Your mood improves 23% on days with morning entries. Consider journaling before 10 AM.
                  </p>
                </div>
              </div>
            </div>
          </motion.div>
        </div>
      </section>

      {/* Features Section */}
      <section id="features" className="py-24 px-6">
        <div className="max-w-6xl mx-auto">
          <motion.div
            initial={{ opacity: 0 }}
            whileInView={{ opacity: 1 }}
            viewport={{ once: true }}
            className="text-center mb-16"
          >
            <h2 className="font-heading text-4xl md:text-5xl text-white mb-4">{t('features_title')}</h2>
            <p className="text-slate-400 text-lg">{t('features_subtitle')}</p>
          </motion.div>

          <div className="grid grid-cols-1 md:grid-cols-2 lg:grid-cols-3 gap-6">
            {features.map((feature, index) => (
              <motion.div
                key={index}
                initial={{ opacity: 0, y: 20 }}
                whileInView={{ opacity: 1, y: 0 }}
                viewport={{ once: true }}
                transition={{ delay: index * 0.1 }}
                className="glass rounded-2xl p-6 hover:border-cyan/30 transition-colors duration-300 group"
              >
                <div className="w-12 h-12 rounded-xl bg-cyan/10 flex items-center justify-center mb-4 group-hover:bg-cyan/20 transition-colors">
                  <feature.icon className="text-cyan" size={24} />
                </div>
                <h3 className="text-white text-xl font-medium mb-2">{feature.title}</h3>
                <p className="text-slate-400">{feature.description}</p>
              </motion.div>
            ))}
          </div>
        </div>
      </section>

      {/* Benefits Section */}
      <section className="py-24 px-6 bg-navy-900/30">
        <div className="max-w-6xl mx-auto">
          <motion.div
            initial={{ opacity: 0 }}
            whileInView={{ opacity: 1 }}
            viewport={{ once: true }}
            className="text-center mb-16"
          >
            <h2 className="font-heading text-4xl md:text-5xl text-white mb-4">{t('benefits_title')}</h2>
            <p className="text-slate-400 text-lg">{t('benefits_subtitle')}</p>
          </motion.div>

          <div className="grid grid-cols-1 sm:grid-cols-2 lg:grid-cols-4 gap-6">
            {benefits.map((benefit, index) => (
              <motion.div
                key={index}
                initial={{ opacity: 0, scale: 0.9 }}
                whileInView={{ opacity: 1, scale: 1 }}
                viewport={{ once: true }}
                transition={{ delay: index * 0.1 }}
                className="text-center p-6"
              >
                <p className="text-5xl font-heading text-cyan mb-2">{benefit.stat}</p>
                <p className="text-slate-300">{benefit.text}</p>
              </motion.div>
            ))}
          </div>
        </div>
      </section>

      {/* CTA Section */}
      <section className="py-24 px-6">
        <div className="max-w-3xl mx-auto text-center">
          <motion.div
            initial={{ opacity: 0, y: 20 }}
            whileInView={{ opacity: 1, y: 0 }}
            viewport={{ once: true }}
          >
            <h2 className="font-heading text-4xl md:text-5xl text-white mb-4">
              {t('cta_title')} <span className="text-gold">{t('cta_title_2')}</span>?
            </h2>
            <p className="text-slate-400 text-lg mb-8">
              {t('cta_subtitle')}
            </p>
            
            <form onSubmit={handleBetaSignup} className="flex flex-col sm:flex-row gap-4 max-w-md mx-auto">
              <Input
                type="email"
                placeholder={t('cta_placeholder')}
                value={email}
                onChange={(e) => setEmail(e.target.value)}
                className="flex-1 bg-navy-900 border-white/10 text-white placeholder:text-slate-500 rounded-full px-6"
                data-testid="beta-email-input"
              />
              <Button 
                type="submit"
                disabled={loading}
                className="bg-gold text-navy-950 hover:bg-gold-muted rounded-full px-8 font-semibold shadow-[0_0_20px_rgba(212,160,60,0.3)]"
                data-testid="beta-signup-btn"
              >
                {loading ? t('cta_signing') : t('cta_button')}
              </Button>
            </form>
          </motion.div>
        </div>
      </section>

      {/* Footer */}
      <footer className="border-t border-white/5 py-8 px-6">
        <div className="max-w-6xl mx-auto flex flex-col md:flex-row items-center justify-between gap-4">
          <div className="flex items-center gap-3">
            <img 
              src="https://customer-assets.emergentagent.com/job_7d9bc2e5-ff38-4e06-b101-9f0eb41c2616/artifacts/w350as9h_LOGOvrn-02.png" 
              alt="VerbNotes" 
              className="h-10"
            />
          </div>
          <p className="text-slate-500 text-sm">
            © 2026 VerbNotes. Liber Nostra d.o.o. All rights reserved.
          </p>
          <div className="flex items-center gap-6">
            <Link to="/privacy" className="text-slate-400 hover:text-cyan text-sm" data-testid="footer-privacy-link">{t('footer_privacy')}</Link>
            <Link to="/terms" className="text-slate-400 hover:text-cyan text-sm" data-testid="footer-terms-link">{t('footer_terms')}</Link>
            <Link to="/contact" className="text-slate-400 hover:text-cyan text-sm" data-testid="footer-contact-link">{t('footer_contact')}</Link>
          </div>
        </div>
      </footer>
    </div>
  );
}
